// utils/TaskUtils.ts - Utility functions and helper methods
import { /*Task,*/ AuditLogEntry, AdditionalReviewerSelection, User } from './TaskTypes';

// Helper functions for reviewer selection
export const hasSelectedReviewers = (reviewerSelection: AdditionalReviewerSelection | null): boolean => {
    return !!(reviewerSelection && reviewerSelection.selectedReviewers && reviewerSelection.selectedReviewers.length > 0);
};

export const getSelectedReviewers = (reviewerSelection: AdditionalReviewerSelection | null): User[] => {
    return reviewerSelection?.selectedReviewers || [];
};

// NEW: Utility functions for the new fields
export const getRequestTypeLabel = (requestType: string | undefined): string => {
    switch (requestType) {
        case 'new': return 'New Request';
        case 'supplementary': return 'Supplementary Request';
        default: return 'Unknown Request Type';
    }
};

export const getPassTypeLabel = (passType: string | undefined): string => {
    switch (passType) {
        case 'single_visit': return 'Single Visit';
        case 'short_term': return 'Short Term';
        case 'long_term': return 'Long Term';
        default: return 'Unknown Pass Type';
    }
};

export const getPassTypeColor = (passType: string | undefined): string => {
    switch (passType) {
        case 'single_visit': return '#3b82f6';  // Blue
        case 'short_term': return '#f59e0b';   // Orange
        case 'long_term': return '#374151';    // Green
        default: return '#6b7280';             // Gray
    }
};

export const getRequestTypeColor = (requestType: string | undefined): string => {
    switch (requestType) {
        case 'new': return '#374151';          // Green
        case 'supplementary': return '#8b5cf6'; // Purple
        default: return '#6b7280';             // Gray
    }
};

export const formatDateRange = (startDate: string | undefined, endDate: string | undefined): string => {
    if (!startDate && !endDate) return 'No date range specified';
    if (!startDate) return `Until ${formatDate(endDate!)}`;
    if (!endDate) return `From ${formatDate(startDate)}`;
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const isDateRangeValid = (startDate: string | undefined, endDate: string | undefined): boolean => {
    if (!startDate || !endDate) return true;
    return new Date(startDate) <= new Date(endDate);
};

export const isDateRangeActive = (startDate: string | undefined, endDate: string | undefined): boolean => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    
    if (start && now < start) return false; // Not started yet
    if (end && now > end) return false;     // Already ended
    return true;
};

// Task type and priority utilities
export const getTaskTypeLabel = (taskType: string): string => {
    switch (taskType) {
        case 'COMPANY_APPROVER': return 'Company Approval';
        case 'COMPANY_REVIEWER': return 'Company Review';
        case 'GATE_APPROVER': return 'Gate Approval';
        case 'GATE_REVIEWER': return 'Gate Review';
        case 'SECURITY_APPROVER': return 'Security Approval';
        default: return taskType;
    }
};

export const getPriorityLabel = (priority: number): string => {
    switch (priority) {
        case 3: return 'Urgent';
        case 2: return 'High';
        default: return 'Normal';
    }
};

export const getPriorityColor = (priority: number): string => {
    switch (priority) {
        case 3: return '#ef4444';
        case 2: return '#F3694A';
        default: return '#6b7280';
    }
};

// Stage information utilities
export const getStageInfo = (taskType: string) => {
    switch (taskType) {
        case 'COMPANY_APPROVER':
            return {
                label: 'Company Approval',
                icon: '🏢',
                description: 'Initial company approval stage',
                allowsExclusions: false,
                explanation: 'At this stage, verify that the request details are in line with the contract requirements. Approve or reject the full request based on it.'
            };
        case 'COMPANY_REVIEWER':
            return {
                label: 'Company Review',
                icon: '👥',
                description: 'Company review and refinement stage',
                allowsExclusions: true,
                explanation: 'The company approver has endorsed this request. Verify that the details align with the contract requirements. You may exclude specific items while approving the rest.'
            };
        case 'GATE_APPROVER':
            return {
                label: 'Gate Approval',
                icon: '🚪',
                description: 'Gate-specific approval stage',
                allowsExclusions: false,
                explanation: 'At this stage, all items must be approved or rejected as a whole.'
            };
        case 'GATE_REVIEWER':
            return {
                label: 'Gate Review',
                icon: '📝',
                description: 'Final gate review and verification stage',
                allowsExclusions: true,
                explanation: 'You can exclude specific items while approving the rest.'
            };
        case 'SECURITY_APPROVER':
            return {
                label: 'Security Approval',
                icon: '🔐',
                description: 'Final security clearance stage',
                allowsExclusions: false,
                explanation: 'All gate approvals have been completed. Review the request and provide your security approval to finalize the gate pass.'
            };
        default:
            return {
                label: taskType,
                icon: '📋',
                description: 'Processing stage',
                allowsExclusions: false,
                explanation: 'Standard approval process.'
            };
    }
};

// Workflow state display utilities
export const getWorkflowStateDisplay = (state: string): { label: string; color: string; icon: string } => {
    const stateMap: { [key: string]: { label: string; color: string; icon: string } } = {
        'DRAFT': { label: 'Draft', color: '#6b7280', icon: '📄' },
        'SUBMITTED': { label: 'Submitted', color: '#3b82f6', icon: '📤' },
        'COMPANY_APPROVER_PENDING': { label: 'Company Approval Pending', color: '#f59e0b', icon: '⏳' },
        'COMPANY_REVIEWER_PENDING': { label: 'Company Review Pending', color: '#F3694A', icon: '👀' },
        'GATE_APPROVER_PENDING': { label: 'Gate Approval Pending', color: '#06b6d4', icon: '🚪' },
        'GATE_REVIEWER_PENDING': { label: 'Gate Review Pending', color: '#8b5cf6', icon: '📝' },
        'SECURITY_APPROVER_PENDING': { label: 'Security Approval Pending', color: '#7c3aed', icon: '🔐' },
        'PARTIALLY_APPROVED': { label: 'Partially Approved', color: '#6b7280', icon: '✅' },
        'FULLY_APPROVED': { label: 'Accepted for Processing', color: '#374151', icon: '🎉' },
        'REJECTED': { label: 'Rejected', color: '#ef4444', icon: '❌' },
        'RETURNED': { label: 'Returned', color: '#ec4899', icon: '↩️' },
        'CANCELLED': { label: 'Cancelled', color: '#6b7280', icon: '🚫' },
        'EXPIRED': { label: 'Expired', color: '#374151', icon: '⏰' }
    };
    return stateMap[state] || { label: state, color: '#6b7280', icon: '❓' };
};

// Date formatting utilities
export const formatDate = (dateString: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const formatDateOnly = (dateString: string | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

export const isTaskOverdue = (dueDate: string): boolean => {
    return new Date(dueDate) < new Date();
};

// Audit log utilities
export const getActionLabel = (actionType: string): string => {
    const labelMap: { [key: string]: string } = {
        'CREATE': 'Created',
        'APPROVE': 'Approved',
        'REJECT': 'Rejected',
        'SUBMIT': 'Submitted',
        'CANCEL': 'Cancelled',
        'UPDATE': 'Updated',
        'ASSIGN': 'Assigned',
        'ESCALATE': 'Escalated',
        'RETURN': 'Returned',
        'COMPLETE': 'Completed',
        'SECURITY_APPROVE': 'Security Approved'
    };
    return labelMap[actionType] || actionType;
};

export const getActionIcon = (actionType: string): string => {
    const iconMap: { [key: string]: string } = {
        'CREATE': '📄',
        'APPROVE': '✅',
        'REJECT': '❌',
        'SUBMIT': '📤',
        'CANCEL': '🚫',
        'UPDATE': '📝',
        'ASSIGN': '👤',
        'ESCALATE': '⬆️',
        'RETURN': '↩️',
        'COMPLETE': '✅',
        'SECURITY_APPROVE': '🔐'
    };
    return iconMap[actionType] || '📋';
};

export const getActionColor = (actionType: string): string => {
    const colorMap: { [key: string]: string } = {
        'CREATE': '#3b82f6',
        'APPROVE': '#374151',
        'REJECT': '#ef4444',
        'SUBMIT': '#06b6d4',
        'CANCEL': '#6b7280',
        'UPDATE': '#f59e0b',
        'ASSIGN': '#8b5cf6',
        'ESCALATE': '#F3694A',
        'RETURN': '#ec4899',
        'COMPLETE': '#374151',
        'SECURITY_APPROVE': '#7c3aed'
    };
    return colorMap[actionType] || '#6b7280';
};

export const formatPerformerInfo = (log: AuditLogEntry): string => {
    const name = log.displayName || log.performedByName || 
                (log.displayEmail ? log.displayEmail.split('@')[0] : null) ||
                (log.performedByEmail ? log.performedByEmail.split('@')[0] : null) ||
                (log.performedBy && log.performedBy.includes('@') ? log.performedBy.split('@')[0] : log.performedBy);

    const email = log.displayEmail || log.performedByEmail || 
                 (log.performedBy && log.performedBy.includes('@') ? log.performedBy : null);

    const company = log.displayCompanyName || log.performedByCompanyName;

    let displayString = name || 'Unknown User';

    if (email && email !== name) {
        displayString += ` (${email})`;
    }

    if (company && company !== 'Unknown Company') {
        displayString += ` - ${company}`;
    }

    return displayString;
};